<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: "Produkte" navigation point — selectable product list (left) + per-product config tabs
    (right). Every tab body is @if-guarded on the active product tab (CONFIG-022); the tab strip
    only renders once a product is selected.
-->
<div class="cw-products p-2">
    <div class="grid">

        <!-- Left: product selection -->
        <div class="col-12 lg:col-4 xl:col-3">
            @if (productListWidget) {
                <mvs-widget
                    [widgetData]="productListWidget"
                    (onObjectSelect)="handleProductSelect($event)">
                </mvs-widget>
            }
        </div>

        <!-- Right: selected product -->
        <div class="col-12 lg:col-8 xl:col-9">
            @if (!selectedProductId) {
                <div class="cw-hint p-5 text-center text-500">Bitte ein Produkt wählen.</div>
            } @else {
                <p-tabs [value]="productActiveTab" (valueChange)="onProductTabChange($event)" [scrollable]="true">
                    <p-tablist>
                        <p-tab value="basis">Basis</p-tab>
                        <p-tab value="bipro">Bipro</p-tab>
                        <p-tab value="fields">Felder</p-tab>
                        <p-tab value="tariffs">Tarifvarianten</p-tab>
                        <p-tab value="baseCoverages">Basisdeckungen</p-tab>
                        <p-tab value="durations">Vertragslaufzeiten</p-tab>
                        <p-tab value="coverages">Elementarprodukte</p-tab>
                        <p-tab value="persons">Personen</p-tab>
                        <p-tab value="objects">Objekte</p-tab>
                        <p-tab value="questions">Fragen</p-tab>
                        <p-tab value="formerContracts">Vorherige Verträge</p-tab>
                        <p-tab value="businessTypes">Geschäftsarten</p-tab>
                        <p-tab value="partnerRestricts">Gesellschaften/Vertragstypen</p-tab>
                    </p-tablist>

                    <p-tabpanels>

                        <!-- Basis -->
                        <p-tabpanel value="basis">
                            @if (productActiveTab === 'basis') {

                                <!--
                                  [CW-LOCAL-CASES] Klassifikationsband der gespeicherten Zeile.
                                  Es beantwortet die eine Frage, die die Produktliste links nicht
                                  beantworten kann: WORAN ist diese Zeile gebunden? Eine Zeile ohne
                                  Produkt (Betriebsmodus „Gesellschaft", „Gesellschaft +
                                  Vertragstyp", „Frei") hat kein Produkt anzuzeigen — ohne dieses
                                  Band bliebe sie schlicht leer.
                                -->
                                <div class="flex flex-wrap align-items-center gap-3 mb-3">
                                    <span class="inline-flex align-items-center gap-2">
                                        <i class="fa-regular fa-sliders text-color-secondary"></i>
                                        <span class="text-color-secondary text-sm">Betriebsmodus</span>
                                        <span class="font-semibold">{{ modeLabel(selectedProductMode) }}</span>
                                    </span>
                                    @if (selectedProductProductName) {
                                        <span class="inline-flex align-items-center gap-2">
                                            <span class="text-color-secondary text-sm">Produkt</span>
                                            <span class="font-medium">{{ selectedProductProductName }}</span>
                                        </span>
                                    }
                                    @if (selectedProductPartnerName) {
                                        <span class="inline-flex align-items-center gap-2">
                                            <span class="text-color-secondary text-sm">Gesellschaft</span>
                                            <span class="font-medium">{{ selectedProductPartnerName }}</span>
                                        </span>
                                    }
                                    @if (selectedProductContractTypeName) {
                                        <span class="inline-flex align-items-center gap-2">
                                            <span class="text-color-secondary text-sm">Vertragstyp</span>
                                            <span class="font-medium">{{ selectedProductContractTypeName }}</span>
                                        </span>
                                    }
                                </div>

                                <div class="flex justify-content-end mb-2">
                                    <p-button
                                        [label]="editProductBasic ? 'Ansicht' : 'Bearbeiten'"
                                        [icon]="editProductBasic ? 'fa-regular fa-eye' : 'fa-regular fa-pen'"
                                        severity="secondary"
                                        [text]="true"
                                        (onClick)="toggleProductBasicEdit()">
                                    </p-button>
                                </div>
                                @if (!editProductBasic && productBasicDataWidget) {
                                    <mvs-widget [widgetData]="productBasicDataWidget"></mvs-widget>
                                }
                                @if (editProductBasic && productBasicEditWidget) {

                                    <!--
                                      [CW-LOCAL-CASES] Regelwerk des gewählten Betriebsmodus (§1.6).
                                      Es steht hier und nicht als Spaltenüberschrift, weil es eine
                                      REGEL ist: Das Formular blendet die Felder aus, die der Modus
                                      verbietet, aber „Pflicht" lässt sich über den Form-Overwrite
                                      nicht stempeln — durchgesetzt wird sie serverseitig vom
                                      CwCaseTypeProductValidator. Damit die Pflicht nicht erst beim
                                      Speichern sichtbar wird, steht sie hier.
                                    -->
                                    <p-message severity="info" styleClass="w-full mb-3">
                                        <div class="flex flex-column gap-2">
                                            <span class="font-semibold">
                                                Betriebsmodus „{{ modeLabel(productFormMode) }}" — was diese Zeile tragen muss
                                            </span>
                                            <div class="flex flex-wrap gap-3">
                                                @for (requirement of productModeRequirements; track requirement.label) {
                                                    <span class="inline-flex align-items-center gap-2 text-sm">
                                                        @if (requirement.state === 'required') {
                                                            <i class="fa-regular fa-asterisk"></i>
                                                            <span><b>{{ requirement.label }}</b> ist Pflicht</span>
                                                        } @else {
                                                            <i class="fa-regular fa-minus"></i>
                                                            <span>{{ requirement.label }} muss leer bleiben</span>
                                                        }
                                                        @if (requirement.conflict) {
                                                            <span class="font-medium">— derzeit gefüllt, bitte leeren</span>
                                                        }
                                                    </span>
                                                }
                                            </div>
                                            @if (productModeChangedInForm) {
                                                <span class="text-sm">
                                                    Der gewählte Betriebsmodus weicht vom gespeicherten
                                                    („{{ modeLabel(selectedProductMode) }}") ab. Felder, die der neue
                                                    Modus verbietet, bleiben so lange sichtbar, bis sie geleert sind —
                                                    ein ausgeblendetes Feld würde seinen alten Wert weiterhin mitsenden
                                                    und das Speichern scheitern lassen.
                                                </span>
                                            }
                                            <span class="text-sm">
                                                Ab Betriebsmodus „Gesellschaft" trägt die <b>Bezeichnung</b> die
                                                Identität der Zeile: Seed-Bundles adressieren sie ohne Produkt über
                                                Antragstyp&nbsp;+&nbsp;Bezeichnung. Ein späteres Umbenennen findet
                                                deshalb kein bestehendes Bundle mehr wieder.
                                            </span>
                                        </div>
                                    </p-message>

                                    <mvs-widget
                                        [widgetData]="productBasicEditWidget"
                                        (onFormDataChange)="handleProductBasicFormChange($event)"
                                        (onChangedObject)="handleProductBasicChangedObject($event)">
                                    </mvs-widget>
                                }
                            }
                        </p-tabpanel>

                        <!-- Bipro -->
                        <p-tabpanel value="bipro">
                            @if (productActiveTab === 'bipro' && productBiproWidget) {
                                <mvs-widget [widgetData]="productBiproWidget"></mvs-widget>
                            }
                        </p-tabpanel>

                        <!-- Felder -->
                        <p-tabpanel value="fields">
                            @if (productActiveTab === 'fields' && productFieldWidget) {
                                <mvs-widget [widgetData]="productFieldWidget"></mvs-widget>
                            }
                        </p-tabpanel>

                        <!-- Tarifvarianten -->
                        <p-tabpanel value="tariffs">
                            @if (productActiveTab === 'tariffs') {
                                <p-message
                                    severity="secondary"
                                    text="Die hier freigeschalteten Tarifvarianten sind eine Teilmenge der Tarifvarianten des Produkts. Ob eine Tarifvariante gewählt werden muss, steuert das Feld „Tarifvariante erforderlich“ im Reiter „Basis“."
                                    styleClass="w-full mb-3">
                                </p-message>
                                @if (productTariffWidget) {
                                    <mvs-widget [widgetData]="productTariffWidget"></mvs-widget>
                                }
                            }
                        </p-tabpanel>

                        <!-- Basisdeckungen -->
                        <p-tabpanel value="baseCoverages">
                            @if (productActiveTab === 'baseCoverages') {
                                <p-message
                                    severity="secondary"
                                    text="Die hier freigeschalteten Basisdeckungen sind eine Teilmenge der Basisdeckungen des Produkts. Ob eine Basisdeckung gewählt werden muss, steuert das Feld „Basisdeckung erforderlich“ im Reiter „Basis“."
                                    styleClass="w-full mb-3">
                                </p-message>
                                @if (productBaseCoverageWidget) {
                                    <mvs-widget [widgetData]="productBaseCoverageWidget"></mvs-widget>
                                }
                            }
                        </p-tabpanel>

                        <!-- Vertragslaufzeiten dieser Produktlinie. Steht neben Tarifvarianten und
                             Basisdeckungen, weil alle drei flache Auswahllisten der Produktlinie sind;
                             ab „Elementarprodukte“ beginnen die Detail-Reiter. -->
                        <p-tabpanel value="durations">
                            @if (productActiveTab === 'durations') {
                                <p-message
                                    severity="secondary"
                                    text="Sobald hier mindestens eine Laufzeit steht, gilt für diese Produktlinie ausschließlich diese Liste — die Vertragslaufzeiten des Vorgangstyps werden dann nicht mehr herangezogen (Übersteuerung, keine Schnittmenge). Ohne Zeile gelten die Laufzeiten des Vorgangstyps. Bei „Feste Laufzeit“ ist „Laufzeit (Jahre)“ Pflicht (mindestens 1 Jahr), bei „Unbefristet“ muss das Feld leer bleiben; „Unbefristet“ ist je Produktlinie nur einmal zulässig."
                                    styleClass="w-full mb-3">
                                </p-message>
                                @if (productDurationWidget) {
                                    <mvs-widget [widgetData]="productDurationWidget"></mvs-widget>
                                }
                            }
                        </p-tabpanel>

                        <!-- Elementarprodukte -->
                        <p-tabpanel value="coverages">
                            @if (productActiveTab === 'coverages') {
                                <div class="grid">
                                    <div class="col-12 lg:col-5">
                                        @if (productCoverageWidget) {
                                            <mvs-widget
                                                [widgetData]="productCoverageWidget"
                                                (onObjectSelect)="handleProductCoverageSelect($event)">
                                            </mvs-widget>
                                        }
                                    </div>
                                    <div class="col-12 lg:col-7">
                                        @if (selectedProductCoverageId) {
                                            @if (productCoverageFieldWidget) {
                                                <mvs-widget class="block mb-3" [widgetData]="productCoverageFieldWidget"></mvs-widget>
                                            }
                                            <!-- Abhängigkeit des Bausteins von Tarifvariante / Basisdeckung.
                                                 Die Zeilen hängen am Baustein, nicht am Produkt — deshalb steht
                                                 die Tabelle hier und nicht als eigener Produkt-Reiter. -->
                                            @if (productCoverageAllowanceWidget) {
                                                <p-message severity="secondary" styleClass="w-full mb-3">
                                                    <span class="block text-sm line-height-3"
                                                          pTooltip="Leer gelassene Felder bedeuten „gilt für alle“. Ohne Zeile gilt der Baustein uneingeschränkt."
                                                          tooltipPosition="bottom">
                                                        <span class="block font-medium mb-1">Wann gilt dieser Baustein?</span>
                                                        <span class="block">
                                                            Jede Zeile ist ein <strong>Erlaubnis-Eintrag</strong>: Sie
                                                            beschreibt eine Kombination, für die der Baustein angeboten
                                                            wird. Mehrere Zeilen ergänzen sich (oder-verknüpft).
                                                        </span>
                                                        <span class="block mt-2">
                                                            &bull; <strong>Keine Zeile</strong> = keine Einschränkung,
                                                            der Baustein gilt immer.
                                                        </span>
                                                        <span class="block">
                                                            &bull; Feld <strong>„Tarifvariante“ leer</strong> = gilt für
                                                            alle Tarifvarianten.
                                                        </span>
                                                        <span class="block">
                                                            &bull; Feld <strong>„Basisdeckung“ leer</strong> = gilt für
                                                            alle Basisdeckungen.
                                                        </span>
                                                        <span class="block">
                                                            &bull; <strong>Beide Felder leer</strong> ist unzulässig —
                                                            das ist bedeutungsgleich mit „keine Zeile“ und wird von der
                                                            Konfigurationsprüfung als Fehler gemeldet.
                                                        </span>
                                                        <span class="block mt-2">
                                                            Welche Achsen überhaupt beteiligt sind, legt das Feld
                                                            „Abhängigkeitsmodus“ am Baustein fest.
                                                        </span>
                                                    </span>
                                                </p-message>
                                                <mvs-widget [widgetData]="productCoverageAllowanceWidget"></mvs-widget>
                                            }
                                        } @else {
                                            <div class="cw-hint p-4 text-center text-500">Bitte ein Elementarprodukt wählen.</div>
                                        }
                                    </div>
                                </div>
                            }
                        </p-tabpanel>

                        <!-- Personen -->
                        <p-tabpanel value="persons">
                            @if (productActiveTab === 'persons') {
                                <div class="grid">
                                    <div class="col-12 lg:col-5">
                                        @if (productPersonWidget) {
                                            <mvs-widget
                                                [widgetData]="productPersonWidget"
                                                (onObjectSelect)="handleProductPersonSelect($event)">
                                            </mvs-widget>
                                        }
                                    </div>
                                    <div class="col-12 lg:col-7">
                                        @if (selectedProductPersonId) {
                                            <!-- [CW-PERSON-REFACTOR] The Person→Felder level was removed with
                                                 cw.CwCaseTypeProductPersonField: CwCaseTypeProductPerson is pure
                                                 configuration again and carries no field pool. The surviving pool is
                                                 cw.CwCaseTypePersonField on the ROLE, edited in Übersicht > Personen;
                                                 its runtime values land in cw.CwCaseProductPersonField. The message
                                                 below keeps the removal from reading as a lost feature. -->
                                            <p-message severity="secondary" styleClass="w-full mb-3">
                                                <span class="block text-sm line-height-3">
                                                    <span class="block font-medium mb-1">Wo werden die Personenfelder gepflegt?</span>
                                                    <span class="block">
                                                        Zusätzliche Felder einer Person werden <strong>nicht mehr je
                                                        Produkt</strong> gepflegt, sondern <strong>einmal an der
                                                        Personenrolle</strong> — unter <strong>Übersicht &rsaquo; Personen
                                                        &rsaquo; Personenfelder</strong>.
                                                    </span>
                                                    <span class="block mt-2">
                                                        Sie gelten dort für <strong>jede</strong> Produktlinie, in der die
                                                        Rolle besetzt wird. Eine Definition genügt — sie muss nicht je
                                                        Produkt wiederholt werden.
                                                    </span>
                                                </span>
                                            </p-message>
                                            <!-- Wann wird diese Person überhaupt gebraucht? Die Zeilen hängen an
                                                 der Produkt-Person, nicht am Produkt — deshalb steht die Tabelle
                                                 hier und nicht als eigener Produkt-Reiter. -->
                                            @if (productPersonAllowanceWidget) {
                                                <p-message severity="secondary" styleClass="w-full mb-3">
                                                    <span class="block text-sm line-height-3"
                                                          pTooltip="Leer gelassene Felder bedeuten „gilt für alle“. Ohne Zeile wird die Person uneingeschränkt benötigt."
                                                          tooltipPosition="bottom">
                                                        <span class="block font-medium mb-1">Wann wird diese Person benötigt?</span>
                                                        <span class="block">
                                                            Jede Zeile ist ein <strong>Erlaubnis-Eintrag</strong>: Sie
                                                            beschreibt eine Kombination, für die die Person benötigt
                                                            wird. Mehrere Zeilen ergänzen sich (oder-verknüpft).
                                                        </span>
                                                        <span class="block mt-2">
                                                            &bull; <strong>Keine Zeile</strong> = keine Einschränkung,
                                                            die Person wird immer benötigt.
                                                        </span>
                                                        <span class="block">
                                                            &bull; Feld <strong>„Tarifvariante“ leer</strong> = gilt für
                                                            alle Tarifvarianten.
                                                        </span>
                                                        <span class="block">
                                                            &bull; Feld <strong>„Basisdeckung“ leer</strong> = gilt für
                                                            alle Basisdeckungen.
                                                        </span>
                                                        <span class="block">
                                                            &bull; <strong>Beide Felder leer</strong> ist unzulässig —
                                                            das ist bedeutungsgleich mit „keine Zeile“ und wird von der
                                                            Konfigurationsprüfung als Fehler gemeldet.
                                                        </span>
                                                    </span>
                                                </p-message>
                                                <mvs-widget class="block mb-3" [widgetData]="productPersonAllowanceWidget"></mvs-widget>
                                            }
                                            <!-- Pflichtangaben zur Person: welche Attribute aus pm.Person /
                                                 pm.PersonDetail diese Rolle IN DIESEM PRODUKT braucht und unter
                                                 welchem Alias ihr Live-Wert veröffentlicht wird. Das
                                                 vorgangsweite Gegenstück ist cw.CwCaseTypePersonReqField;
                                                 beide Ebenen gelten, die strengere gewinnt. -->
                                            @if (productPersonReqFieldWidget) {
                                                <p-message
                                                    severity="secondary"
                                                    text="Diese Liste legt fest, welche Angaben zur Person für DIESE Produktlinie benötigt werden. Sie ergänzt die vorgangsweite Liste unter Übersicht › Personen: Beide Ebenen werden ausgewertet, und die strengere Forderung gewinnt — eine Produktlinie kann eine Angabe verschärfen, nie lockern. Ohne Zeile gilt für diese Produktlinie keine zusätzliche Forderung."
                                                    styleClass="w-full mb-3">
                                                </p-message>
                                                <mvs-widget class="block mb-3" [widgetData]="productPersonReqFieldWidget"></mvs-widget>
                                            }
                                            <div class="grid">
                                                <div class="col-12 lg:col-6">
                                                    @if (productPersonCoverageWidget) {
                                                        <mvs-widget
                                                            [widgetData]="productPersonCoverageWidget"
                                                            (onObjectSelect)="handleProductPersonCoverageSelect($event)">
                                                        </mvs-widget>
                                                    }
                                                </div>
                                                <div class="col-12 lg:col-6">
                                                    @if (productPersonCoverageFieldWidget) {
                                                        <mvs-widget [widgetData]="productPersonCoverageFieldWidget"></mvs-widget>
                                                    }
                                                </div>
                                            </div>
                                            <!-- Bezugsrechte der Rolle (Bezugsrecht → Felder). Die Felder hängen
                                                 am einzelnen Bezugsrecht, nicht an der Rolle — deshalb erst
                                                 auswählen, dann pflegen. -->
                                            <div class="grid">
                                                <div class="col-12 lg:col-6">
                                                    @if (productBeneficiaryWidget) {
                                                        <mvs-widget
                                                            [widgetData]="productBeneficiaryWidget"
                                                            (onObjectSelect)="handleProductBeneficiarySelect($event)">
                                                        </mvs-widget>
                                                    }
                                                </div>
                                                <div class="col-12 lg:col-6">
                                                    @if (productBeneficiaryFieldWidget) {
                                                        <mvs-widget [widgetData]="productBeneficiaryFieldWidget"></mvs-widget>
                                                    } @else {
                                                        <div class="cw-hint p-4 text-center text-500">Bitte ein Bezugsrecht wählen.</div>
                                                    }
                                                </div>
                                            </div>
                                        } @else {
                                            <div class="cw-hint p-4 text-center text-500">Bitte eine Person wählen.</div>
                                        }
                                    </div>
                                </div>
                            }
                        </p-tabpanel>

                        <!-- Objekte -->
                        <p-tabpanel value="objects">
                            @if (productActiveTab === 'objects') {
                                <div class="grid">
                                    <div class="col-12 lg:col-5">
                                        @if (productObjectWidget) {
                                            <mvs-widget
                                                [widgetData]="productObjectWidget"
                                                (onObjectSelect)="handleProductObjectSelect($event)">
                                            </mvs-widget>
                                        }
                                    </div>
                                    <div class="col-12 lg:col-7">
                                        @if (selectedProductObjectId) {
                                            <!-- Wann wird dieses Objekt überhaupt gebraucht? Die Zeilen hängen am
                                                 Objekt, nicht am Produkt — deshalb steht die Tabelle hier und
                                                 nicht als eigener Produkt-Reiter. -->
                                            @if (productObjectAllowanceWidget) {
                                                <p-message severity="secondary" styleClass="w-full mb-3">
                                                    <span class="block text-sm line-height-3"
                                                          pTooltip="Leer gelassene Felder bedeuten „gilt für alle“. Ohne Zeile wird das Objekt uneingeschränkt benötigt."
                                                          tooltipPosition="bottom">
                                                        <span class="block font-medium mb-1">Wann wird dieses Objekt benötigt?</span>
                                                        <span class="block">
                                                            Jede Zeile ist ein <strong>Erlaubnis-Eintrag</strong>: Sie
                                                            beschreibt eine Kombination, für die das Objekt benötigt
                                                            wird. Mehrere Zeilen ergänzen sich (oder-verknüpft).
                                                        </span>
                                                        <span class="block mt-2">
                                                            &bull; <strong>Keine Zeile</strong> = keine Einschränkung,
                                                            das Objekt wird immer benötigt.
                                                        </span>
                                                        <span class="block">
                                                            &bull; Feld <strong>„Tarifvariante“ leer</strong> = gilt für
                                                            alle Tarifvarianten.
                                                        </span>
                                                        <span class="block">
                                                            &bull; Feld <strong>„Basisdeckung“ leer</strong> = gilt für
                                                            alle Basisdeckungen.
                                                        </span>
                                                        <span class="block">
                                                            &bull; <strong>Beide Felder leer</strong> ist unzulässig —
                                                            das ist bedeutungsgleich mit „keine Zeile“ und wird von der
                                                            Konfigurationsprüfung als Fehler gemeldet.
                                                        </span>
                                                    </span>
                                                </p-message>
                                                <mvs-widget class="block mb-3" [widgetData]="productObjectAllowanceWidget"></mvs-widget>
                                            }
                                            <!-- Definitions-Zuordnungen: Zeilen, die KEIN Produktfeld belegen,
                                                 sondern direkt am Objekt hängen. Sie erklären ein Feld des
                                                 Versicherungsobjekttyps als benötigt und stellen seinen Wert
                                                 unter eigenem Alias bereit. Gleiche Entität wie die
                                                 Zielfeld-Zuordnungen weiter unten, andere Zeilenart. -->
                                            @if (productObjectDefinitionIotFieldWidget) {
                                                <p-message
                                                    severity="secondary"
                                                    text="Diese Liste hängt direkt am Objekt und belegt KEIN Feld des Vorgangstyps. Jede Zeile erklärt ein Feld des Versicherungsobjekttyps als benötigt und kann seinen Wert unter einem eigenen Alias im Austauschmodell bereitstellen. Zuordnungen auf ein konkretes Produktfeld werden dagegen unten beim jeweiligen Feld gepflegt."
                                                    styleClass="w-full mb-3">
                                                </p-message>
                                                <mvs-widget class="block mb-3" [widgetData]="productObjectDefinitionIotFieldWidget"></mvs-widget>
                                            }
                                            <!-- Felder des Objekts und — je gewähltem Feld — die Zuordnungen auf
                                                 die Felder des Versicherungsobjekttyps. Ein Klick auf eine
                                                 Feldzeile öffnet die Zuordnungstabelle darunter. -->
                                            @if (productObjectFieldWidget) {
                                                <mvs-widget
                                                    class="block mb-3"
                                                    [widgetData]="productObjectFieldWidget"
                                                    (onObjectSelect)="handleProductObjectFieldSelect($event)">
                                                </mvs-widget>
                                            }
                                            @if (selectedProductObjectFieldId && productObjectFieldIotFieldWidget) {
                                                <p-message
                                                    severity="secondary"
                                                    text="Diese Liste legt fest, aus welchem Feld des Versicherungsobjekts das GEWÄHLTE Objektfeld vorbelegt wird — je konkretem Versicherungsobjekttyp eine Zeile. „Zuordnungsverhalten = BIND“ schreibt den Wert zusätzlich auf das Versicherungsobjekt zurück. Ohne Zeile wird das Feld nicht vorbelegt. Zeilen ohne Zielfeld gehören in die Definitions-Zuordnungen weiter oben."
                                                    styleClass="w-full mb-3">
                                                </p-message>
                                                <mvs-widget class="block mb-3" [widgetData]="productObjectFieldIotFieldWidget"></mvs-widget>
                                            }
                                            <div class="grid">
                                                <div class="col-12 lg:col-6">
                                                    @if (productObjectCoverageWidget) {
                                                        <mvs-widget
                                                            [widgetData]="productObjectCoverageWidget"
                                                            (onObjectSelect)="handleProductObjectCoverageSelect($event)">
                                                        </mvs-widget>
                                                    }
                                                </div>
                                                <div class="col-12 lg:col-6">
                                                    @if (productObjectCoverageFieldWidget) {
                                                        <mvs-widget [widgetData]="productObjectCoverageFieldWidget"></mvs-widget>
                                                    }
                                                </div>
                                            </div>
                                        } @else {
                                            <div class="cw-hint p-4 text-center text-500">Bitte ein Objekt wählen.</div>
                                        }
                                    </div>
                                </div>
                            }
                        </p-tabpanel>

                        <!-- Fragen (Fragensektion → Fragen). The product-side question family lives
                             HERE and not in the case-level Fragen tab: its FK is caseTypeProduct. -->
                        <p-tabpanel value="questions">
                            @if (productActiveTab === 'questions') {
                                <div class="grid">
                                    <div class="col-12 lg:col-5">
                                        @if (productQuestionSectionWidget) {
                                            <mvs-widget
                                                [widgetData]="productQuestionSectionWidget"
                                                (onObjectSelect)="handleProductQuestionSectionSelect($event)">
                                            </mvs-widget>
                                        }
                                    </div>
                                    <div class="col-12 lg:col-7">
                                        @if (productQuestionWidget) {
                                            <mvs-widget [widgetData]="productQuestionWidget"></mvs-widget>
                                        } @else {
                                            <div class="cw-hint p-4 text-center text-500">Bitte eine Fragensektion wählen.</div>
                                        }
                                    </div>
                                </div>
                            }
                        </p-tabpanel>

                        <!-- Vorherige Verträge -->
                        <p-tabpanel value="formerContracts">
                            @if (productActiveTab === 'formerContracts') {
                                <div class="grid">
                                    <div class="col-12 lg:col-6">
                                        @if (productFormerContractTypeWidget) {
                                            <mvs-widget [widgetData]="productFormerContractTypeWidget"></mvs-widget>
                                        }
                                    </div>
                                    <div class="col-12 lg:col-6">
                                        @if (productFormerProductWidget) {
                                            <mvs-widget [widgetData]="productFormerProductWidget"></mvs-widget>
                                        }
                                    </div>
                                </div>
                            }
                        </p-tabpanel>

                        <!-- Geschäftsarten dieser Produktzeile. Das produktseitige Gegenstück zur
                             vorgangsweiten Liste (Übersicht > Geschäftsarten): Der Vorgang kann ein
                             Neuvertrag sein, während eine seiner Produktzeilen eine Umdeckung ist —
                             beide Ebenen gelten nebeneinander, keine schränkt die andere ein. -->
                        <p-tabpanel value="businessTypes">
                            @if (productActiveTab === 'businessTypes') {
                                <p-message
                                    severity="secondary"
                                    text="Diese Liste legt fest, in welchen Geschäftsarten (Neuvertrag, Umschreibung, Umdeckung) DIESE Produktzeile verkauft werden darf. Sie gilt unabhängig von den Geschäftsarten des Vorgangstyps: Der Vorgang kann ein Neuvertrag sein, während eine Produktzeile eine Umdeckung ist. Ohne Zeile gilt für diese Produktzeile keine Einschränkung."
                                    styleClass="w-full mb-3">
                                </p-message>
                                <!-- [cw Vorvertrag-Erweiterung / R9] Master-Detail wie Elementarprodukte: links die
                                     Geschäftsarten der Produktzeile, rechts deren „Felder" — die Angaben, die der
                                     Vorvertrag-Schritt bei genau dieser Geschäftsart abfragt. -->
                                <div class="grid">
                                    <div class="col-12 lg:col-5">
                                        @if (productBusinessTypeWidget) {
                                            <mvs-widget
                                                [widgetData]="productBusinessTypeWidget"
                                                (onObjectSelect)="handleProductBusinessTypeSelect($event)"
                                                (onObjectDeselect)="handleProductBusinessTypeDeselect()">
                                            </mvs-widget>
                                        }
                                    </div>
                                    <div class="col-12 lg:col-7">
                                        @if (selectedProductBusinessTypeId) {
                                            <!-- [R9 Audit-Fix] Basis der gewählten Geschäftsart: Ansicht/Bearbeiten wie
                                                 Produkte › Basis — die Attribute der Zeile (Vorvertrag-Kündigung,
                                                 Prüfmodus, Kündigungsart/-grund, Vertragsbasis, Bezeichnung, aktiv …)
                                                 bleiben so auch nach dem Anlegen pflegbar. -->
                                            <div class="flex justify-content-between align-items-center gap-2 mb-2">
                                                <span class="font-medium">
                                                    @if (selectedProductBusinessTypeName) {
                                                        {{ selectedProductBusinessTypeName }}
                                                    }
                                                </span>
                                                <p-button
                                                    [label]="editProductBusinessType ? 'Ansicht' : 'Bearbeiten'"
                                                    [icon]="editProductBusinessType ? 'fa-regular fa-eye' : 'fa-regular fa-pen'"
                                                    severity="secondary"
                                                    [text]="true"
                                                    (onClick)="toggleProductBusinessTypeEdit()">
                                                </p-button>
                                            </div>
                                            @if (!editProductBusinessType && productBusinessTypeDataWidget) {
                                                <mvs-widget [widgetData]="productBusinessTypeDataWidget"></mvs-widget>
                                            }
                                            @if (editProductBusinessType && productBusinessTypeEditWidget) {
                                                <mvs-widget
                                                    [widgetData]="productBusinessTypeEditWidget"
                                                    (onChangedObject)="handleProductBusinessTypeChangedObject($event)">
                                                </mvs-widget>
                                            }
                                            @if (productBusinessTypeFieldWidget) {
                                                <div class="mt-4">
                                                    <mvs-widget [widgetData]="productBusinessTypeFieldWidget"></mvs-widget>
                                                </div>
                                            }
                                        } @else {
                                            <p-message severity="secondary" styleClass="w-full"
                                                       text="Geschäftsart links auswählen, um ihre Felder zu pflegen. Die Felder erscheinen im Vorvertrag-Schritt nur bei dieser Geschäftsart — und nur, wenn mindestens ein Feld aktiv ist.">
                                            </p-message>
                                        }
                                    </div>
                                </div>
                            }
                        </p-tabpanel>

                        <!-- Erlaubte Kombinationen Gesellschaft/Vertragstyp/(Produkt) dieser
                             Produktzeile. Jede Zeile erlaubt GENAU EINE Kombination; solange keine
                             Zeile existiert, gilt keine Einschränkung und die Produktzeile bietet
                             weiterhin alle Gesellschaften und Vertragstypen an. -->
                        <p-tabpanel value="partnerRestricts">
                            @if (productActiveTab === 'partnerRestricts') {
                                <p-message
                                    severity="secondary"
                                    text="Diese Liste legt fest, mit welchen Kombinationen aus Gesellschaft, Vertragstyp und optional Produkt DIESE Produktzeile verkauft werden darf. Jede Zeile erlaubt genau eine Kombination; eine Zeile ohne Produkt erlaubt das Paar aus Gesellschaft und Vertragstyp als Ganzes. Ohne Zeile gilt für diese Produktzeile keine Einschränkung — alle Gesellschaften und Vertragstypen bleiben wählbar."
                                    styleClass="w-full mb-3">
                                </p-message>
                                @if (productPartnerRestrictWidget) {
                                    <mvs-widget [widgetData]="productPartnerRestrictWidget"></mvs-widget>
                                }
                            }
                        </p-tabpanel>

                    </p-tabpanels>
                </p-tabs>
            }
        </div>

    </div>
</div>
